import { z } from "zod";

import { normalizeUsername } from "@pharmacy/domain";

import { ONE_TARGET, oneTarget, phoneOrEmail } from "./signup";

/**
 * One kind of credential, two ways of naming yourself.
 *
 *   admin  verified phone or email + password — created at signup, recoverable by code
 *   staff  username + password — created by an admin, reset by one
 *
 * Staff used to carry a six-digit PIN instead. That was defensible only while
 * it worked solely on a till an admin had enrolled them on; once people sign in
 * from their own phones the same secret answers from anywhere, and six digits
 * is not a remote credential. So there is one kind of secret now, and the only
 * difference between the roles is which identifier finds the account.
 *
 * Both fail identically. A different message for "no such account" and "wrong
 * secret" would tell somebody probing which numbers and usernames exist.
 */

export const usernameSchema = z
  .string()
  .trim()
  .min(3)
  .max(30)
  .transform((v) => normalizeUsername(v));

/**
 * A staff password. Four characters is the floor, by product decision.
 *
 * Deliberately weaker than the eight an owner must use, because the two protect
 * different things: an owner's password reaches billing, user management and
 * the activity log, while a cashier's reaches the till and the branches they
 * work in. What makes four survivable is that guessing is answered by a lock
 * that does not expire on its own — eight wrong tries and only an owner can
 * clear it — and by a per-address limit in front of the whole endpoint.
 *
 * Not trimmed: a password is bytes the person chose, and silently eating a
 * leading space makes a credential that works on one client and not another.
 */
export const staffPasswordSchema = z
  .string()
  .min(4, "At least 4 characters")
  .max(200, "Too long");

/** An owner signs in with their phone OR their proven email, plus password. */
export const adminLoginRequest = z
  .object({ ...phoneOrEmail, password: z.string().min(1, "Enter your password") })
  .refine(oneTarget, ONE_TARGET);

/**
 * No device. A staff member signs in on their own phone, and the account is
 * found from the username alone — which is why usernames are globally unique.
 */
export const staffLoginRequest = z.object({
  username: usernameSchema,
  password: z.string().min(1, "Enter your password"),
});

export const refreshRequest = z.object({
  refreshToken: z.string().min(1),
});

export const tokenPairResponse = z.object({
  accessToken: z.string(),
  refreshToken: z.string(),
  expiresInSeconds: z.number().int().positive(),
  refreshExpiresInSeconds: z.number().int().positive(),
});

export type AdminLoginRequest = z.infer<typeof adminLoginRequest>;
export type StaffLoginRequest = z.infer<typeof staffLoginRequest>;
export type RefreshRequest = z.infer<typeof refreshRequest>;
export type TokenPairResponse = z.infer<typeof tokenPairResponse>;
